import { FlatPatientWithActigraphyReports } from '@/store/modules/patient-with-actigraphy-reports/getters'
import * as R from 'ramda'
import { DataPoint } from '@/jsonapi/CommonTypes'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import {
  ONE_DAY_TIMESTAMP,
  ONE_MINUTE_TIMESTAMP,
} from '@/components/patient-stats/graphs/mapping-chart-options/constants'
import { buildAxis } from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import { getStartOfDay, removeAbbr } from '@/utils/time'
import XAxis = echarts.EChartOption.XAxis
import YAxis = echarts.EChartOption.YAxis

const DAY_START_TIME_24H = 12
const MAX_Y = 600
const MINS_OF_ONE_HOUR = 60
const GAP_THRESHOLD_MIN = 5

type RangeDataPoint = {
  from: DataPoint
  to: DataPoint
}

type DailyGroupedData = {
  [key: string]: {
    activityCounts: DataPoint[]
    activityGaps: RangeDataPoint[]
    sleepingPeriods: RangeDataPoint[]
    transitionalPeriods: RangeDataPoint[]
  }
}

type ItemStyle = {
  color: string
}

type AreaStyles = {
  activityAreaStyle: ItemStyle
  sleepingAreaStyle: ItemStyle
  transitionalAreaStyle: ItemStyle
  gapAreaStyle: ItemStyle
}

function makeAreaStyles(params: { colors: any }): AreaStyles {
  const { colors } = params
  return {
    activityAreaStyle: {
      color: colors.actigraphyAwake,
    },
    transitionalAreaStyle: {
      color: colors.actigraphyTransionPeriod,
    },
    sleepingAreaStyle: {
      color: colors.actigraphySleeping,
    },
    gapAreaStyle: {
      color: colors.actigraphyNoData,
    },
  }
}

export function buildActigraphyOptions(params: {
  actigraphyReport: FlatPatientWithActigraphyReports
  startTimestamp: number
  endTimestamp: number
  timezone: string
  colors: any // vuetify colors
}): any | echarts.EChartOption<echarts.EChartOption.SeriesLine>[] {
  // any type of return is temporary, type file is probably broken
  const { actigraphyReport, startTimestamp, endTimestamp, timezone, colors } = params
  const { activityCounts, sleepStates } = actigraphyReport
  const startTimestampsOfDays = getXTimestmaps({
    startTimestamp,
    endTimestamp,
    timezone,
  })
  // TODO for now, data has duplicated data
  const uniqSleepStates = R.uniqBy((dp: DataPoint) => `${dp.timestamp}${dp.value}`, sleepStates)

  const dataDayMap = createDailyGroupedData({
    timezone: timezone,
    timestamps: startTimestampsOfDays,
    sleepStates: uniqSleepStates,
    activityCounts: activityCounts,
  })

  const areaStyles = makeAreaStyles({ colors })

  const maxX = ONE_DAY_TIMESTAMP / ONE_MINUTE_TIMESTAMP

  return Object.entries(dataDayMap).map(([key, value]) => {
    return {
      animation: false,
      grid: [{ x: '0', y: '2.5%', width: '100%', height: '95%', containLabel: true } as echarts.EChartOption.Grid],
      xAxis: {
        type: 'category',
        data: R.range(0, maxX),
        min: 0,
        max: maxX,
        axisLine: { show: true },
        splitLine: { show: false },
        axisTick: {
          show: true,
          alignWithLabel: false,
          interval: MINS_OF_ONE_HOUR - 1,
        },
        axisLabel: {
          show: true,
          align: 'left',
          showMinLabel: true,
          showMaxLabel: true,
          interval: 59,
          formatter: param => {
            const pointOfHalfDay: number = maxX * 0.5
            if (param === pointOfHalfDay.toString()) {
              return moment(parseInt(key, 10) + pointOfHalfDay * ONE_MINUTE_TIMESTAMP)
                .tz(timezone)
                .format(removeAbbr(timezone, CHART_DATE_FORMAT.HOUR_ABBR))
            }
            if (param === '0') {
              return moment(parseInt(key, 10))
                .tz(timezone)
                .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))
            }
            return null
          },
          overwriteOption: {
            type: 'category',

            axisTick: {
              show: true,
              // interval: MINS_OF_ONE_HOUR - 1,
              interval: MINS_OF_ONE_HOUR - 1,
            },
          },
        },
      } as XAxis,
      yAxis: buildAxis({
        axisType: 'y',
        maxValue: MAX_Y,
      }) as YAxis,
      series: [
        {
          type: 'bar',
          silent: true,
          showSymbol: false,
          label: { show: false },
          itemStyle: {
            width: 1,
            color: colors.black,
            opacity: 0.9,
          },
          // @ts-ignore
          data: value.activityCounts.map(dp => ({
            value: [(dp.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, dp.value],
          })),
          markArea: {
            // @ts-ignore
            data: [
              [
                {
                  coord: [0, 0],
                  itemStyle: areaStyles.activityAreaStyle,
                },
                {
                  coord: [maxX, MAX_Y],
                  itemStyle: areaStyles.activityAreaStyle,
                },
              ],
              ...value.sleepingPeriods.map(dpSet => {
                return [
                  {
                    coord: [(dpSet.from.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, 0],
                    itemStyle: areaStyles.sleepingAreaStyle,
                  },
                  {
                    coord: [(dpSet.to.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, MAX_Y],
                    itemStyle: areaStyles.sleepingAreaStyle,
                  },
                ]
              }),
              ...value.transitionalPeriods.map(dpSet => {
                return [
                  {
                    coord: [(dpSet.from.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, 0],
                    itemStyle: areaStyles.transitionalAreaStyle,
                  },
                  {
                    coord: [(dpSet.to.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, MAX_Y],
                    itemStyle: areaStyles.transitionalAreaStyle,
                  },
                ]
              }),
              ...value.activityGaps.map(dpSet => {
                return [
                  {
                    coord: [(dpSet.from.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, 0],
                    itemStyle: areaStyles.gapAreaStyle,
                  },
                  {
                    coord: [(dpSet.to.timestamp - parseInt(key, 10)) / ONE_MINUTE_TIMESTAMP, MAX_Y],
                    itemStyle: areaStyles.gapAreaStyle,
                  },
                ]
              }),
            ],
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: colors.red,
            opacity: 0.65,
          },
        },
        formatter: params => {
          const timestamp = parseInt(key, 10) + params[0].value[0] * ONE_MINUTE_TIMESTAMP
          return moment(timestamp)
            .tz(timezone)
            .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))
        },
      },
    }
  })
}

function getXTimestmaps(param: { timezone: string; endTimestamp: number; startTimestamp: number }) {
  const { timezone, endTimestamp, startTimestamp } = param
  const result = []
  let timestamp = getStartOfDay({
    baseHour: DAY_START_TIME_24H,
    timezone,
    timestamp: startTimestamp,
  })

  const diffDays = (endTimestamp - startTimestamp) / ONE_DAY_TIMESTAMP

  for (let i = 0; i < diffDays; i++) {
    result.push(timestamp)
    timestamp += ONE_DAY_TIMESTAMP
  }

  return result
}

function tryAddingData(dailyGrouped: DailyGroupedData, startDp, endDp, key: keyof DailyGroupedData, timezone): void {
  const fromStartOfDay = getStartOfDay({
    timestamp: startDp.timestamp,
    timezone,
    baseHour: DAY_START_TIME_24H,
  })
  const toStartOfDay = getStartOfDay({
    timestamp: endDp.timestamp,
    timezone,
    baseHour: DAY_START_TIME_24H,
  })
  const isBeyondToday = fromStartOfDay !== toStartOfDay

  if (isBeyondToday) {
    if (dailyGrouped[fromStartOfDay] != null) {
      dailyGrouped[fromStartOfDay][key].push({
        from: startDp,
        to: {
          timestamp: toStartOfDay,
          value: endDp.value,
        },
      })
    }

    if (dailyGrouped[toStartOfDay] != null) {
      dailyGrouped[toStartOfDay][key].push({
        from: {
          timestamp: fromStartOfDay,
          value: startDp.value,
        },
        to: endDp,
      })
    }
  } else {
    if (dailyGrouped[fromStartOfDay] != null) {
      dailyGrouped[fromStartOfDay][key].push({
        from: startDp,
        to: endDp,
      })
    }
  }
}

function createDailyGroupedData(options: {
  timestamps: number[]
  sleepStates: DataPoint[]
  activityCounts: DataPoint[]
  timezone: string
}): DailyGroupedData {
  const { timestamps, sleepStates, activityCounts, timezone } = options
  const dailyGroupedData: DailyGroupedData = timestamps.reduce((acc, cur) => {
    acc[cur.toString()] = {
      activityCounts: [],
      activityGaps: [],
      sleepingPeriods: [],
      transitionalPeriods: [],
    }
    return acc
  }, {})

  let prevActivityDp: DataPoint = null
  activityCounts.forEach(dp => {
    const startOfDay: string = getStartOfDay({
      timestamp: dp.timestamp,
      timezone,
      baseHour: DAY_START_TIME_24H,
    }).toString()
    if (dailyGroupedData[startOfDay]) {
      dailyGroupedData[startOfDay].activityCounts.push(dp)

      // handle gap for first dp in day
      if (dailyGroupedData[startOfDay].activityCounts.length === 1) {
        // console.log({ startOfDay, ts: dp.timestamp })
        prevActivityDp = { timestamp: parseInt(startOfDay, 10), value: null }
      }
    }

    if (prevActivityDp) {
      const diff = dp.timestamp - prevActivityDp.timestamp
      const isGap = diff > ONE_MINUTE_TIMESTAMP * GAP_THRESHOLD_MIN

      if (isGap) {
        tryAddingData(dailyGroupedData, prevActivityDp, dp, 'activityGaps', timezone)
      }
    }

    prevActivityDp = dp
  })

  // add gap after last dp
  if (activityCounts.length) {
    const lastDp = activityCounts[activityCounts.length - 1]
    const lastDpStartOfDay = getStartOfDay({
      timestamp: lastDp.timestamp,
      timezone,
      baseHour: DAY_START_TIME_24H,
    })
    if (dailyGroupedData[lastDpStartOfDay.toString()]) {
      dailyGroupedData[lastDpStartOfDay.toString()].activityGaps.push({
        from: { timestamp: lastDp.timestamp, value: 0 },
        to: { timestamp: lastDpStartOfDay + ONE_DAY_TIMESTAMP, value: 0 },
      })
    }
  }
  // handle empty day
  Object.entries(dailyGroupedData).forEach(([key, value]) => {
    if (!value.activityCounts.length) {
      dailyGroupedData[key].activityGaps.push({
        from: {
          timestamp: parseInt(key, 10),
          value: 0,
        },
        to: {
          timestamp: parseInt(key, 10) + ONE_DAY_TIMESTAMP,
          value: 0,
        },
      })
    }
  })

  let startDp = null
  let endDp = null
  sleepStates.forEach((dp, index) => {
    if (dp.value === 0) {
      startDp = dp
    }

    // first data does not definitely start with 0
    if (index === 0 && dp.value !== 0) {
      const startOfDay: string = getStartOfDay({
        timestamp: dp.timestamp,
        timezone,
        baseHour: DAY_START_TIME_24H,
      }).toString()
      startDp = {
        timestamp: startOfDay,
        value: 0,
      }
    }

    if (dp.value === 0.1 && startDp.value === 0) {
      endDp = dp
      tryAddingData(dailyGroupedData, startDp, endDp, 'transitionalPeriods', timezone)
      startDp = dp
      endDp = null
    } else if (dp.value === 0.9 && startDp.value === 0.1) {
      endDp = dp
      tryAddingData(dailyGroupedData, startDp, endDp, 'sleepingPeriods', timezone)
      startDp = dp
    } else if (dp.value === 1 && startDp.value === 0.9) {
      endDp = dp
      tryAddingData(dailyGroupedData, startDp, endDp, 'transitionalPeriods', timezone)
      startDp = null
      endDp = null
    }
  })

  return dailyGroupedData
}
